import React, { useCallback } from 'react';

/* eslint frontbucket-patterns/no-new-emotion-styled: "warn" */
import styled from '@emotion/styled';

import { CommentAuthor } from '@atlaskit/comment';
import { NextEditorPlugin } from '@atlaskit/editor-common/types';
import { ExtensionProvidersProp } from '@atlaskit/editor-core/editor';
import { token } from '@atlaskit/tokens';

import { FabricUser } from 'src/components/bbc-atlaskit-editor';
import {
  Conversation as BaseConversation,
  ConversationProps as BaseConversationProps,
} from 'src/components/conversation';
import { ApiCommentOrPlaceholder } from 'src/components/conversation-provider/types';
import { toBBUser } from 'src/components/conversation-provider/utils';
import { UserAvatar } from 'src/containers/user-avatar';
import { UserName } from 'src/containers/user-name';
import {
  ConversationsContextInterface,
  useConversationsContext,
} from 'src/contexts/conversations-context';
import { usePortalContainer } from 'src/hooks/use-portal-container';
import { buildSocialAuthLinkWithRedirect } from 'src/utils/buildSocialAuthLinkWithRedirect';

const ConversationWrapper = styled.div({
  paddingTop: 0,
  '& input': {
    '::placeholder': {
      color: token('color.text.subtlest'),
    },
  },
});

type ExtraProps = {
  portalParentId: string;
  extensionProviders?: ExtensionProvidersProp;
  codeSuggestionsExtensionHandler?: (
    commentId: number,
    apiComment: ApiCommentOrPlaceholder | undefined
  ) => any;
  editorPlugins?: [NextEditorPlugin<any>, boolean][];
  getSuggestions?: () => Record<string, string>;
};

// We grab the conversationsContext in this component using the hook, so we don't need it to be passed in here.
export type Props = Omit<BaseConversationProps, 'conversationsContext'> &
  ExtraProps;

// Establish to TypeScript that the name is `string` and not `string | undefined`
const bbUser = (user?: FabricUser) =>
  user && user.id && user.name ? toBBUser({ ...user, name: user.name }) : null;

export const Conversation = (props: Props) => {
  const portal = usePortalContainer(props.portalParentId);

  const loginLink = buildSocialAuthLinkWithRedirect(location.pathname);

  const renderAvatar = useCallback(
    (user?: FabricUser) => <UserAvatar user={bbUser(user)} />,
    []
  );

  const renderName = useCallback(
    (user?: FabricUser) => (
      <CommentAuthor>
        <UserName user={bbUser(user)} />
      </CommentAuthor>
    ),
    []
  );

  const conversationsContext: ConversationsContextInterface =
    useConversationsContext();

  return (
    <ConversationWrapper id={props.portalParentId}>
      <BaseConversation
        {...props}
        allowHelpButton
        portal={portal}
        renderAuthorAvatar={renderAvatar}
        renderAuthorName={renderName}
        showBeforeUnloadWarning
        showDeleteCommentDialog
        loginLink={loginLink}
        conversationsContext={conversationsContext}
      />
    </ConversationWrapper>
  );
};
